import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { FilterableItemComponent } from './components/filterable-item';
import { AppliedFilterGroup, FilterBlockData, FilterItem } from './interfaces/types';
import { FilterDropdown } from './components/filter-dropdown';

interface IFilterAppProps {
    id: string;
}

declare global {
    interface Window {
        filterBlocks: FilterBlockData[];
    }
}

const FilterApp: React.FC<IFilterAppProps> = ({ id }) => {
    const filterBlockData: FilterBlockData = window.filterBlocks?.find(
        filterBlock => filterBlock.id === id
    );

    if (!filterBlockData) {
        return null;
    }

    const filters = filterBlockData.content.filters || [];
    const filterableItems = filterBlockData.content.filterableItems || [];

    const [appliedFilterableItems, setAppliedFilterableItems] = useState([...filterableItems]);
    const [appliedFilterCategories, setAppliedFilterCategories] = useState<string[]>([]);
    const [appliedFilterGroups, setAppliedFilterGroups] = useState<AppliedFilterGroup[]>([]);
    const [currentlySelectedFiltersGroups, setCurrentlySelectedFiltersGroups] = useState<AppliedFilterGroup[]>([]);
    const [currentlySelectedFilterCategories, setCurrentlySelectedFilterCategories] = useState<string[]>([]);

    const defaultNumberOfItemsToShow = filterBlockData.content.defaultNumberOfItemsToShow;
    const showMoreButtonText = filterBlockData.content.showMoreButtonText || "Show All";
    const hasAppliedFilters = appliedFilterCategories.length > 0;
    const itemsToDisplay = hasAppliedFilters ? appliedFilterableItems : filterableItems;
    const [showAll, setShowAll] = useState<boolean>(false);
    const hasFilters = filters.length > 0 && filters.some(filterDropdown => filterDropdown.choices?.length > 0);

    useEffect(() => {
        updateAppliedFilterableItems();
    }, [appliedFilterGroups, appliedFilterCategories]);

    const resetFilters = () => {
        setCurrentlySelectedFiltersGroups([]);
        setCurrentlySelectedFilterCategories([]);
        setAppliedFilterCategories([]);
        setAppliedFilterGroups([]);
        setAppliedFilterableItems(filterableItems);
    };

    const updateFilterCategories = (appliedFilterGroups: AppliedFilterGroup[]) => {
        const currentAppliedFilterCategories = appliedFilterGroups.map(
            (appliedFilterGroup) => appliedFilterGroup.category
        );
        setCurrentlySelectedFilterCategories(currentAppliedFilterCategories);
    };

    const updateAppliedFilterableItems = () => {
        let filteredItems = filterableItems
            .filter(filterableItem =>
                appliedFilterCategories.every(category => filterableItem.categories.includes(category))
            )
            .filter(filterableItem => filterableItem.categories.length > 0 || appliedFilterCategories.length === 0);
        setAppliedFilterableItems(filteredItems);
    };

    const updateFilter = (category: FilterItem, selectedFilter: string) => {
        const currentSelectedFiltersGroups = [...currentlySelectedFiltersGroups];

        if (currentSelectedFiltersGroups.filter(
            filter => filter.categoriesGroupName === category.label
        ).length === 1 || selectedFilter === "") {
            currentSelectedFiltersGroups.splice(
                currentSelectedFiltersGroups.findIndex(
                    filter => filter.categoriesGroupName === category.label
                ),
                1
            );
        }

        if (selectedFilter !== "") {
            currentSelectedFiltersGroups.push({
                categoriesGroupName: category.label,
                category: selectedFilter
            });
        }

        const updatedFilterGroups = [...currentSelectedFiltersGroups];
        updateFilterCategories(updatedFilterGroups);
        setCurrentlySelectedFiltersGroups(updatedFilterGroups);
    };

    const clearFilters = () => {
        setShowAll(false);
        resetFilters();
    };

    useEffect(() => {
        setAppliedFilterCategories(currentlySelectedFilterCategories);
        setAppliedFilterGroups(currentlySelectedFiltersGroups);
    }, [currentlySelectedFilterCategories, currentlySelectedFiltersGroups]);

    return (
        <div className="stack-lg">
            {hasFilters && (
                <div className="stack-sm">
                    <div className="filter-options">
                        {filters.map((filter, index) =>
                            filter.choices?.length > 0 ? (
                                <FilterDropdown
                                    key={index}
                                    index={index}
                                    filter={filter}
                                    currentlySelectedFiltersGroups={currentlySelectedFiltersGroups}
                                    updateFilter={updateFilter}
                                    id={`${id}-${index}`}
                                />
                            ) : null
                        )}

                        {appliedFilterCategories.length > 0 && (
                            <button
                                className="btn-primary btn-nowrap"
                                type="button"
                                onClick={clearFilters}
                            >
                                {filterBlockData.content.clearFiltersLabel}
                            </button>
                        )}
                    </div>
                </div>
            )}
            {filterBlockData.content.hideTheDisplayOfQuantityResults !== true && (
                <p className="small bold">
                    Displaying {showAll
                        ? itemsToDisplay.length
                        : Math.min(defaultNumberOfItemsToShow, itemsToDisplay.length)} / {itemsToDisplay.length}
                </p>
            )}

            {itemsToDisplay.length > 0 ? (
                <ul role="list" className={`layout layout-${filterBlockData.content.displayIn2Columns ? "2" : "3"}-eq`}>
                    {itemsToDisplay.map((filterableItem, index) => (
                        (index < defaultNumberOfItemsToShow || showAll) ? (
                            <li key={filterableItem.id + index}>
                                <FilterableItemComponent item={filterableItem} />
                            </li>
                        ) : null
                    ))}
                </ul>
            ) : (
                /* Items are filtered out server-side once they fall outside their
                   visibility window, so an empty list has two distinct causes.
                   Telling a visitor to update filters they never applied is
                   misleading, and there is nothing they could do about it. */
                <p>
                    {hasAppliedFilters
                        ? "No items match your criteria. Please update your filters."
                        : "No items are currently available."}
                </p>
            )}

            {itemsToDisplay.length > defaultNumberOfItemsToShow && !showAll && (
                <div className="filter-more">
                    <button
                        className="btn-primary btn-full-width-mobile btn-icon-r icon-plus"
                        type="button"
                        onClick={() => setShowAll(true)}
                    >
                        {showMoreButtonText}
                    </button>
                </div>
            )}
        </div>
    );
};

export function FilterAppInit(appRoot: HTMLElement) {
    const root = ReactDOM.createRoot(appRoot);
    root.render(
        <React.StrictMode>
            <FilterApp id={appRoot.getAttribute('id')?.toString() || ''} />
        </React.StrictMode>
    );
}
